import type { Leaderboard, SessionState, SlidePosition, Tally } from "@hali-shares/deck-schema";
import { useCallback, useEffect, useRef, useState } from "react";
import type { StartedSession } from "../lib/api";
import { type LiveSocket, connectLive } from "./socket";

export type PresenterSession = {
  code: string;
  connected: boolean;
  state: SessionState | null;
  tallies: Record<string, Tally>;
  leaderboard: Leaderboard | null;
  goto: (pos: SlidePosition) => void;
  setLocked: (blockId: string, locked: boolean) => void;
  setRevealed: (blockId: string, revealed: boolean) => void;
  end: () => void;
};

/** Presenter side of a live session: one socket, the server's state and tallies, and the presenter's commands. */
export function usePresenterSession(started: StartedSession | null): PresenterSession | null {
  const socket = useRef<LiveSocket | null>(null);
  const [connected, setConnected] = useState(false);
  const [state, setState] = useState<SessionState | null>(null);
  const [tallies, setTallies] = useState<Record<string, Tally>>({});
  const [leaderboard, setLeaderboard] = useState<Leaderboard | null>(null);

  useEffect(() => {
    if (!started) return;
    const s = connectLive({ role: "presenter", code: started.code, presenterToken: started.presenterToken });
    socket.current = s;
    s.on("connect", () => setConnected(true));
    s.on("disconnect", () => setConnected(false));
    s.on("session:state", setState);
    s.on("poll:tally", (t) => setTallies((all) => ({ ...all, [t.blockId]: t })));
    s.on("leaderboard:state", setLeaderboard);
    s.on("live:error", (e) => console.warn(`live: ${e.event}: ${e.message}`));
    return () => {
      s.disconnect();
      socket.current = null;
      setState(null);
      setConnected(false);
    };
  }, [started]);

  const goto = useCallback((pos: SlidePosition) => socket.current?.emit("presenter:goto", pos), []);
  const setLocked = useCallback(
    (blockId: string, locked: boolean) => socket.current?.emit("poll:lock", { blockId, locked }),
    [],
  );
  const setRevealed = useCallback(
    (blockId: string, revealed: boolean) => socket.current?.emit("poll:reveal", { blockId, revealed }),
    [],
  );
  const end = useCallback(() => socket.current?.emit("session:end", {}), []);

  if (!started) return null;
  return { code: started.code, connected, state, tallies, leaderboard, goto, setLocked, setRevealed, end };
}
